/**
 * Zuschnitt-Rechnung für den Banner.
 *
 * Getrennt von der Oberfläche, weil es die Stelle ist, an der man sich verrechnen kann: die
 * Grenzen müssen garantieren, dass das Bild den Rahmen **immer** vollständig füllt. Ein Rand,
 * durch den der Hintergrund blitzt, wäre in einem Zuschnitt-Werkzeug der peinlichste Fehler.
 */

/** Seitenverhältnis des Banners auf der Veranstalterseite (880px Inhaltsbreite bei 170px Höhe). */
export const BANNER_ASPECT = 880 / 170;

/** Ausgabebreite. Deckt Netzhaut-Bildschirme ab und bleibt unter der 2-MB-Grenze des Servers. */
export const BANNER_OUTPUT_WIDTH = 1760;

export interface CropState {
  /** 1 = Bild füllt den Rahmen gerade eben ("cover"). Größer heißt näher heran. */
  zoom: number;
  /** Verschiebung der linken oberen Ecke des skalierten Bildes, in Rahmen-Pixeln (≤ 0). */
  x: number;
  y: number;
}

/** Die Skalierung, bei der das Bild den Rahmen gerade ausfüllt. */
export const coverScale = (frameW: number, frameH: number, imgW: number, imgH: number): number =>
  Math.max(frameW / imgW, frameH / imgH);

/**
 * Hält die Verschiebung in den Grenzen, in denen das Bild den Rahmen noch ganz bedeckt.
 *
 * Ist das skalierte Bild in einer Richtung genau so groß wie der Rahmen, bleibt dort nur die 0 —
 * deshalb `Math.min(0, …)`: ohne das käme bei Rundungsfehlern eine positive Untergrenze heraus
 * und das Bild ruckte um einen Pixel weg.
 */
export const clampOffset = (
  offset: number,
  frameSize: number,
  scaledSize: number,
): number => Math.max(Math.min(0, frameSize - scaledSize), Math.min(0, offset));

/**
 * Rechnet den sichtbaren Ausschnitt in Koordinaten des Originalbildes um — das ist, was auf die
 * Zeichenfläche kommt.
 */
export const sourceRect = (
  frameW: number, frameH: number, scale: number, x: number, y: number,
): { sx: number; sy: number; sw: number; sh: number } => ({
  sx: -x / scale,
  sy: -y / scale,
  sw: frameW / scale,
  sh: frameH / scale,
});
